import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
} from '@angular/core';
import { User } from 'app/models';
import { CommentApi } from 'app/modules/dashboard/api/comment.api';
import {
  CommentsHttpResult,
} from 'app/modules/dashboard/models/comments.model';
import { BehaviorSubject, map, Observable, Subscription, take } from 'rxjs';
import { SkeletonModule } from 'primeng/skeleton';
import { CommentBlockComponent } from './comment-block/comment-block.component';
import { InfiniteScrollDirective } from 'ngx-infinite-scroll';
import { TranslateModule } from '@ngx-translate/core';
import { InputCommentComponent } from '../../../../../../shared/components/input-comment/input-comment.component';
import { NgIf, NgFor, AsyncPipe } from '@angular/common';
import { GpsOnlyService } from 'app/modules/browse/services/gps-only.service';
import { WorkoutService } from 'app/services/workout.service';
import { Comment } from '@kinomap-dev/ng-ui-lib/lib/models/comment.model';

@Component({
  selector: 'app-watch-comment-list',
  templateUrl: './watch-comments-list.component.html',
  styleUrls: ['./watch-comments-list.component.scss'],
  standalone: true,
  imports: [
    NgIf,
    InputCommentComponent,
    TranslateModule,
    InfiniteScrollDirective,
    NgFor,
    CommentBlockComponent,
    SkeletonModule,
    AsyncPipe,
  ],
})

// eslint-disable-next-line @angular-eslint/component-class-suffix
export class WatchCommentList implements OnDestroy {
  @Input() scrollContainerSelector: string;
  @Input() user!: User;
  @Input() focusCommentId: number | null;
  @Input() sessionId: number | undefined;
  @Input() commentType:
    | 'watch'
    | 'scenic'
    | 'coaching'
    | 'activity'
    | 'mapmode'
    | 'workout';
  @Input() commentsCount: number = 0

  @Output() commentAdded: EventEmitter<any>;
  @Output() deleteCommentOutput: EventEmitter<number>;
  @Output() commentsCountEvent: EventEmitter<number>;

  commentsResult$: Observable<CommentsHttpResult | Comment[]>;
  comments$: BehaviorSubject<any[]>;
  loading$: BehaviorSubject<boolean>;

  subscriptions: Subscription[] = [];
  title: string = '';
  videoId: number = 0;
  page: number = 1;
  comments: any[];
  userLogged: boolean = this.user === null ? false : true;
  lastPage: number = 0;
  actionCommentClickId: number = 0;

  constructor(
    private commentApi: CommentApi,
    private gpsOnlyService: GpsOnlyService,
    private workoutService: WorkoutService
  ) {
    this.comments$ = new BehaviorSubject<any[]>(null);
    this.loading$ = new BehaviorSubject<boolean>(true);
    this.commentAdded = new EventEmitter<any>(null);
    this.deleteCommentOutput = new EventEmitter<number>(null);
    this.commentsCountEvent = new EventEmitter<number>(null);
  }

  getComments(id: number, page: number, sessionId: number | undefined) {
    switch (this.commentType) {
      case 'watch':
      case 'scenic':
        this.commentsResult$ = this.commentApi.getCommentsVideo(id, page);
        break;
      case 'coaching':
        this.commentsResult$ = this.commentApi.getCommentsCoaching(id, page);
        break;
      case 'mapmode':
        this.commentsResult$ = this.commentApi.getGpsOnlyComments(id, page);
        break;
      case 'workout':
        this.commentsResult$ = this.commentApi.getCommentsWorkout(id);
        break;
      case 'activity':
        if (id && sessionId) {
          this.commentsResult$ = this.commentApi.getCommentsActivity(
            id,
            sessionId,
            page
          );
        } else {
          console.error(
            `get activity comment error : activityId:${id} - sessionId${sessionId}`
          );
        }
        break;
    }

    this.subscriptions = [
      ...this.subscriptions,
      this.commentsResult$.subscribe((res: CommentsHttpResult | Comment[]) => {
        if ('data' in res) {
          if (this.comments$.getValue() === null) {
            this.comments$.next(res.data);
          } else {
            const updatedComments = [...this.comments$.getValue(), ...res.data];
            this.comments$.next(updatedComments);
          }
          this.lastPage = res.meta.last_page;
          this.loading$.next(false);
          return res;
        } else {
          if (this.comments$.getValue() === null) {
            this.comments$.next(res);
          } else {
            const updatedComments = [...this.comments$.getValue(), ...res];
            this.comments$.next(updatedComments);
          }
        }
      }),
      this.comments$.subscribe((res) => res),
    ];
  }

  initList(id: number) {
    this.videoId = id;
    this.getComments(id, this.page, this.sessionId);
  }

  nextPage() {
    if (this.page < this.lastPage) {
      this.loading$.next(true);
      this.page++;
      this.getComments(this.videoId, this.page, this.sessionId);
    }
  }

  updateList() {
    this.loading$.next(true);
    this.page = 0;
    this.comments$.next([]);
    this.initList(this.videoId);
  }

  onMoreClick(commentId: any) {
    this.actionCommentClickId = commentId;
  }

  moreSubCommentClick(commentId: any) {
    this.actionCommentClickId = commentId;
  }

  onScrollBottom() {
    this.nextPage();
  }

  onSendComment(message: string, user: User) {
    this.comments$.next([
      {
        body: message,
        user: user,
        id: null,
        created_at: new Date().getTime() / 1000,
      },
      ...(this.comments$.getValue() || [])
    ]);

    switch (this.commentType) {
      case 'watch':
      case 'scenic': {
        this.commentApi
          .postCommentVideo(this.videoId, message)
          .pipe(take(1))
          .subscribe((res) => {
            this.updateList();
            this.commentsCountEvent.emit(1);
            this.commentAdded.emit({ body: message, id: res.data.comment_id });
          });
        break;
      }

      case 'coaching':
        this.commentApi
          .postCommentCoaching(this.videoId, message)
          .pipe(take(1))
          .subscribe((res) => {
            this.updateList();
            this.commentsCountEvent.emit(1);
            this.commentAdded.emit({ body: message, id: res.data.comment_id });
          });
        break;

      case 'mapmode':
        this.commentApi
          .postCommentGpsOnly(this.videoId.toString(), message)
          .pipe(take(1))
          .subscribe((res) => {
            this.updateList();
            this.commentsCountEvent.emit(1);
            this.commentAdded.emit({ body: message, id: res.data.comment_id });
          });
        break;

      case 'activity':
        this.commentApi
          .postCommentActivity(this.videoId, this.sessionId, message)
          .pipe(take(1))
          .subscribe((res) => {
            this.updateList();
            this.commentsCountEvent.emit(1);
            this.commentAdded.emit({ body: message, id: res.data.comment_id });
          });
        break;

      case 'workout':
        this.commentApi
          .postCommentWorkout(this.videoId, message)
          .pipe(take(1))
          .subscribe((res) => {
            this.updateList();
            this.commentsCountEvent.emit(1);
            this.commentAdded.emit({ body: message, id: res.data.comment_id });
          });
        break;
    }
  }

  deleteComment(commentId: number) {
    switch (this.commentType) {
      case 'watch':
      case 'scenic': {
        this.commentApi
          .deleteVideo(this.videoId, commentId)
          .pipe(take(1))
          .subscribe(() => {
            if (
              this.comments$.getValue().find((el) => el.id === commentId) !==
              undefined
            ) {
              const updatedComments = this.comments$
                .getValue()
                .filter((el) => el.id !== commentId);
              this.deleteCommentOutput.next(commentId);
              this.commentsCount -= 1;
              this.commentsCountEvent.emit(-1);
              this.comments$.next(updatedComments);
            }
          });
        break;
      }
      case 'coaching':
        this.commentApi
          .deleteCoaching(this.videoId, commentId)
          .pipe(take(1))
          .subscribe(() => {
            if (
              this.comments$.getValue().find((el) => el.id === commentId) !==
              undefined
            ) {
              const updatedComments = this.comments$
                .getValue()
                .filter((el) => el.id !== commentId);
              this.deleteCommentOutput.next(commentId);
              this.commentsCount -= 1;
              this.commentsCountEvent.emit(-1);
              this.comments$.next(updatedComments);
            }
          });
        break;
    }
  }

  subCommentsCountEvent(event: number) {
    this.commentsCountEvent.emit(event);
  }

  ngOnDestroy(): void {
    this.videoId = 0;
    this.subscriptions.forEach((sub: Subscription) => sub.unsubscribe());
  }
}
